// 产品推荐引擎 — 基于 CSV 真实产品 JSON 数据评分
import type { ScenarioId } from '../pages/wizard/ScenarioStep';
import type { RegionId } from '../pages/wizard/RegionStep';
import type { WizardParams } from '../pages/wizard/ParameterStep';
import catalogData from '../data/product-catalog.json';

/** 产品目录条目（来自 CSV 解析） */
export interface CatalogProduct {
  id: string;
  name: string;
  series: string;
  family: string;
  line: string;
  pitch: number;
  brightness: number;
  ledProcess: string;
  refreshRate: number;
  cabinetSizes: string[];
  maintenance: string;
  ipRating: string;
  pciLevel: 'TOP+' | 'TOP' | 'Non-TOP';
  isOutdoor: boolean;
  isIndoor: boolean;
  regions: string[];
  scenarios: string[];
  salesVolume: number;
  orderCount: number;
}

export interface ScoredProduct {
  product: CatalogProduct;
  score: number;
  reasons: string[];
}

// 类型安全地导入 JSON
const catalog: CatalogProduct[] = catalogData as CatalogProduct[];

/** 环境光到推荐亮度（nits） */
const LIGHTING_BRIGHTNESS: Record<string, number> = {
  dark: 500,
  indoor: 800,
  semi_outdoor: 3000,
  outdoor: 5000,
};

/**
 * 对真实产品数据进行评分推荐
 *
 * 三层过滤 + 评分：
 * 1. 硬过滤：区域 + 场景 + 室内/外
 * 2. 软过滤：间距范围
 * 3. 评分排序取 TOP 9
 *
 * 评分维度（满分 100）：
 * - 间距匹配度 (35%): 观看距离/3 = 最佳间距
 * - 亮度匹配度 (25%): 环境光线对应亮度需求
 * - PCI 优先级 (20%): TOP+ > TOP > Non-TOP
 * - 销售热度  (15%): 25年销售额归一化
 * - 刷新率    (5%):  越高越好
 */
export function scoreProducts(
  region: RegionId,
  scenario: ScenarioId,
  params: WizardParams,
): ScoredProduct[] {
  const optimalPitch = params.viewingDistance / 3;
  const targetBrightness = LIGHTING_BRIGHTNESS[params.lighting] ?? 800;
  const isOutdoorScenario = ['outdoor', 'sports'].includes(scenario);

  // Step 1: 硬过滤 — 区域 + 室内外
  let filtered = catalog.filter(p => {
    // 区域匹配（如果产品有区域数据）
    if (p.regions.length > 0 && !p.regions.includes(region)) return false;
    // 室内外匹配
    if (isOutdoorScenario && !p.isOutdoor && p.brightness < 2000) return false;
    if (!isOutdoorScenario && p.isOutdoor && !p.isIndoor) return false;
    return true;
  });

  // Step 2: 软过滤 — 优先场景匹配的产品，但不排除其他
  const withScenario = filtered.filter(p => p.scenarios.includes(scenario));
  if (withScenario.length >= 9) {
    filtered = withScenario;
  }

  // Step 3: 评分
  const maxSales = Math.max(...filtered.map(p => p.salesVolume), 1);

  const scored: ScoredProduct[] = filtered.map(product => {
    const reasons: string[] = [];

    // 间距匹配度 (35%)
    const pitchDiff = Math.abs(product.pitch - optimalPitch);
    const pitchScore = Math.max(0, 100 - (pitchDiff / Math.max(optimalPitch, 0.5)) * 80);
    if (pitchScore >= 80) reasons.push(`P${product.pitch} 间距匹配`);

    // 亮度匹配度 (25%)
    let brightnessScore = 60;
    if (product.brightness > 0) {
      if (product.brightness >= targetBrightness) {
        brightnessScore = 90;
        reasons.push(`${product.brightness}nits 亮度达标`);
      } else {
        brightnessScore = Math.max(20, (product.brightness / targetBrightness) * 80);
      }
    }

    // PCI 优先级 (20%)
    let pciScore = 40;
    if (product.pciLevel === 'TOP+') { pciScore = 100; reasons.push('明星主推'); }
    else if (product.pciLevel === 'TOP') { pciScore = 70; reasons.push('标准方案'); }

    // 销售热度 (15%)
    const salesScore = Math.min(100, (product.salesVolume / maxSales) * 100);
    if (salesScore >= 70) reasons.push('热销产品');

    // 刷新率 (5%)
    const refreshScore = Math.min(100, (product.refreshRate / 7680) * 100);

    const totalScore = Math.round(
      pitchScore * 0.35 +
      brightnessScore * 0.25 +
      pciScore * 0.20 +
      salesScore * 0.15 +
      refreshScore * 0.05
    );

    return {
      product,
      score: Math.min(99, Math.max(10, totalScore)),
      reasons,
    };
  });

  // 排序并去重（同系列只取最佳间距）
  scored.sort((a, b) => b.score - a.score);

  const seen = new Set<string>();
  const deduped: ScoredProduct[] = [];
  for (const item of scored) {
    if (!seen.has(item.product.series)) {
      seen.add(item.product.series);
      deduped.push(item);
    }
    if (deduped.length >= 30) break;
  }

  return deduped;
}
